import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { getVesselIncineratorSummary } from "../../../../services/vesselMachineryService";
import { Flame, Droplets, Trash2, Utensils } from "lucide-react";

const IncineratorData = () => {
  const { startDate, endDate } = useSelector((state) => state.dateRange);
  const { currentVessel } = useSelector((state) => state.vesselOverview);
  const { selectedVoyage } = useSelector((state) => state.voyageFilter); // Add voyage filter

  const [incineratorOverview, setIncineratorOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Default empty data structure
  const defaultIncineratorData = {
    sludgeOil: { value: "0.0" },
    bilgeWaterResidues: { value: "0.0" },
    solidGarbage: { value: "0.0" },
    foodWaste: { value: "0.0" },
  };

  useEffect(() => {
    const fetchIncineratorOverview = async () => {
      // Skip if no vessel selected
      if (!currentVessel?._id) {
        setIncineratorOverview(defaultIncineratorData);
        return;
      }

      // Skip if no voyage selected AND no date range
      if (!selectedVoyage && (!startDate || !endDate)) {
        setIncineratorOverview(defaultIncineratorData);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await getVesselIncineratorSummary(
          currentVessel._id,
          selectedVoyage ? null : startDate, // Pass null for dates if voyage selected
          selectedVoyage ? null : endDate, // Pass null for dates if voyage selected
          selectedVoyage?._id || null // Pass voyage ID if selected
        );

        console.log("API Response Incinerator Cards:", response);

        if (response?.status === "success") {
          setIncineratorOverview(response.data || defaultIncineratorData);
        } else {
          setIncineratorOverview(defaultIncineratorData);
          setError(
            response?.message || "Failed to fetch incinerator overview data"
          );
        }
      } catch (err) {
        console.error("Incinerator API Error:", err);
        setError(err.message);
        setIncineratorOverview(defaultIncineratorData);
      } finally {
        setLoading(false);
      }
    };

    fetchIncineratorOverview();
  }, [currentVessel, startDate, endDate, selectedVoyage]); // Add selectedVoyage to dependencies

  // Show loading state
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-xl">
        {[1, 2, 3, 4].map((item) => (
          <div
            key={item}
            className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 animate-pulse"
          >
            <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
            <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
            <div className="h-2 bg-gray-200 rounded w-full mt-3"></div>
          </div>
        ))}
      </div>
    );
  }

  // Helper function to get data with fallbacks to default
  const getData = (key) => {
    return incineratorOverview?.[key] || defaultIncineratorData[key];
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {/* Error message */}
      {error && (
        <div className="col-span-full bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <p className="text-yellow-800 text-sm">API Error: {error}</p>
        </div>
      )}

      {/* Sludge Oil Burning Tile */}
      <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-red-50 rounded-lg">
            <Flame className="w-5 h-5 text-red-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide truncate">
              Sludge Oil
            </h3>
            <div className="flex items-baseline mt-1">
              <span className="text-2xl font-bold text-gray-900">
                {getData("sludgeOil").value}
              </span>
              <span className="ml-1 text-sm text-gray-500">MT</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bilge Water Residues Tile */}
      <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-50 rounded-lg">
            <Droplets className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide truncate">
              Bilge Water Residues
            </h3>
            <div className="flex items-baseline mt-1">
              <span className="text-2xl font-bold text-gray-900">
                {getData("bilgeWaterResidues").value}
              </span>
              <span className="ml-1 text-sm text-gray-500">MT</span>
            </div>
          </div>
        </div>
      </div>

      {/* Solid Garbage Incineration Tile */}
      <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-orange-50 rounded-lg">
            <Trash2 className="w-5 h-5 text-orange-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide truncate">
              Solid Garbage
            </h3>
            <div className="flex items-baseline mt-1">
              <span className="text-2xl font-bold text-gray-900">
                {getData("solidGarbage").value}
              </span>
              <span className="ml-1 text-sm text-gray-500">MT</span>
            </div>
          </div>
        </div>
      </div>

      {/* Food Waste Disposal Tile */}
      <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-green-50 rounded-lg">
            <Utensils className="w-5 h-5 text-green-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide truncate">
              Food Waste
            </h3>
            <div className="flex items-baseline mt-1">
              <span className="text-2xl font-bold text-gray-900">
                {getData("foodWaste").value}
              </span>
              <span className="ml-1 text-sm text-gray-500">MT</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncineratorData;
